/**
 * PROG-SET-MEDIA/FE-SAVE-1 (#0eefd7cd) — recursive FormData appender for
 * exercise- AND per-set media files.
 *
 * Replaces the flat double-loop that used to live inline in
 * `EditProgrammazione.handleSave`. Walks the cloned programmazione payload,
 * extracts every `File` handle from exercise- and set-level `image`/`video`
 * fields, appends them to `FormData` under the canonical bracket-path keys,
 * and deletes them from the payload so the final JSON body carries zero
 * `File` instances inside `splits`.
 *
 * Field-name contract (matches the existing BE upload route):
 *   - splits[i][exercises][j][image|video]
 *   - splits[i][exercises][j][progression][weeks][w][sets][s][image|video]
 *
 * Pure on the payload reference: the function mutates the passed object
 * (in-place `delete`) so callers MUST pass a clone they own. The
 * `EditProgrammazione` save flow already deep-clones exercises (and now
 * sets too) before invoking this helper.
 *
 * The function returns nothing; observable effect is `formData.append(...)`
 * calls plus the in-place key removals on the payload.
 */

type Mutable = Record<string, unknown>;

const isFile = (v: unknown): v is File =>
  typeof File !== "undefined" && v instanceof File;

/**
 * Append set-level media files for a single exercise's `progression.weeks`.
 * Walks `weeks[w].sets[s]` and extracts `image`/`video` File handles.
 */
function appendProgressionMedia(
  splitIndex: number,
  exerciseIndex: number,
  exercise: Mutable,
  formData: FormData,
): void {
  const progression = exercise.progression as Mutable | undefined;
  const weeks = progression?.weeks as Mutable[] | undefined;
  if (!Array.isArray(weeks)) return;
  weeks.forEach((week, weekIndex) => {
    const sets = (week.sets as Mutable[] | undefined) ?? [];
    if (!Array.isArray(sets)) return;
    sets.forEach((set, setIndex) => {
      const base = `splits[${splitIndex}][exercises][${exerciseIndex}][progression][weeks][${weekIndex}][sets][${setIndex}]`;
      if (isFile(set.image)) {
        formData.append(`${base}[image]`, set.image as File);
        delete set.image;
      }
      if (isFile(set.video)) {
        formData.append(`${base}[video]`, set.video as File);
        delete set.video;
      }
      // PROG-SET-MEDIA — per-set role galleries. The server maps each appended
      // file → its item's `key` by the item's index in the array, then drops
      // items without a key. So leave items in place at their index and only
      // strip the transient `file` handle from the JSON.
      (["trainerMedia", "athleteMedia"] as const).forEach((roleKey) => {
        const arr = set[roleKey] as Array<Record<string, unknown>> | undefined;
        if (!Array.isArray(arr)) return;
        arr.forEach((item, k) => {
          if (isFile(item.file)) {
            formData.append(`${base}[${roleKey}][${k}][file]`, item.file as File);
            delete item.file;
          }
        });
      });
    });
  });
}

/**
 * Append exercise- and set-level media for the whole programmazione payload.
 * Mutates `payload.splits[*].exercises[*]` and (when present)
 * `payload.splits[*].exercises[*].progression.weeks[*].sets[*]` to strip
 * `image`/`video` File handles into the supplied `FormData`.
 */
export function appendMediaFiles(payload: Mutable, formData: FormData): void {
  const splits = payload.splits as Mutable[] | undefined;
  if (!Array.isArray(splits)) return;
  splits.forEach((split, splitIndex) => {
    const exercises = (split.exercises as Mutable[] | undefined) ?? [];
    if (!Array.isArray(exercises)) return;
    exercises.forEach((exercise, exerciseIndex) => {
      const base = `splits[${splitIndex}][exercises][${exerciseIndex}]`;
      if (isFile(exercise.image)) {
        formData.append(`${base}[image]`, exercise.image as File);
        delete exercise.image;
      }
      if (isFile(exercise.video)) {
        formData.append(`${base}[video]`, exercise.video as File);
        delete exercise.video;
      }
      appendProgressionMedia(splitIndex, exerciseIndex, exercise, formData);
    });
  });
}
